import React, { useEffect, useState } from 'react'
import { Alert, Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper'
import { LoadingSpinner } from '../../presentation-components/display-elements-components/loading-spinner'
import OrganisationService from '../../../shared/services/organisation/organisation.service'
import { IWttPolicyLockedPeriod, IWttPolicyPeriod, IWttPolicyPreview } from '../../../shared/services/organisation/organisation.model'

interface WttPolicyDialogProps {
  open: boolean;
  /** The setting being switched to: true includes well-to-tank, false excludes it. */
  includeWtt: boolean;
  onClose: () => void;
  /** Saves the setting. Rejects with the server's error when the switch is refused. */
  onConfirm: () => Promise<void>;
}

const LOCKED_PERIOD_THRESHOLD_KG = 5

const formatKg = (value?: number | null): string => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return '-'
  if (Math.abs(numeric) >= 1000) return `${(numeric / 1000).toFixed(2)} t CO2e`
  return `${numeric.toFixed(2)} kg CO2e`
}

const periodLabel = (period: IWttPolicyPeriod | IWttPolicyLockedPeriod): string => {
  if (period.name) return period.name
  const from = period.startDate ? period.startDate.slice(0, 10) : '?'
  const to = period.endDate ? period.endDate.slice(0, 10) : '?'
  return `${from} to ${to}`
}

const groupLabel = (group: IWttPolicyPeriod['byGroup'][number]): string => {
  const title = group.title || 'Other'
  return group.scope3CategoryNumber != null ? `Category ${group.scope3CategoryNumber} (${title})` : title
}

const joinNames = (names: string[]): string => {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

const blockerMessage = (preview: IWttPolicyPreview): string | null => {
  if (!preview.blockers?.length) return null
  const recalculating = preview.blockers.some(b => b.kind === 'FACTOR_YEAR_RECALCULATION')
  const importing = preview.blockers.some(b => b.kind === 'IMPORT_COMMIT')
  const what = recalculating && importing
    ? 'A recalculation and an import are'
    : recalculating ? 'A recalculation is' : 'An import is'
  return `${what} still running for this account. You can change this setting once it has finished.`
}

/**
 * Confirms switching well-to-tank emissions on or off, after showing what the switch does.
 *
 * Both directions move reported totals in every open report period, so nothing is saved until the
 * user has seen the change per period and agreed. The figures come from the same query the
 * switch runs, so what is shown is what happens. Locked periods are named because the switch
 * never reaches them.
 */
export const WttPolicyDialog: React.FC<WttPolicyDialogProps> = ({ open, includeWtt, onClose, onConfirm }) => {
  const [preview, setPreview] = useState<IWttPolicyPreview | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [submitError, setSubmitError] = useState('')

  const organisationService = new OrganisationService()

  useEffect(() => {
    if (!open) return
    setPreview(null)
    setLoadError('')
    setSubmitError('')
    setIsLoading(true)
    organisationService.getWttPolicyPreview(includeWtt)
      .then(response => setPreview(response))
      .catch(() => setLoadError('We could not work out what this would change. Please try again in a moment.'))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, includeWtt])

  const handleClose = () => {
    if (isSubmitting) return
    onClose()
  }

  const handleConfirm = async () => {
    setIsSubmitting(true)
    setSubmitError('')
    try {
      await onConfirm()
    } catch (error: any) {
      // A refusal (4xx) carries a reason worth showing, such as work still running. A server error
      // carries only a generic word, so say something useful instead.
      const status = Number(error?.response?.status)
      const reason = status >= 400 && status < 500 ? error?.response?.data?.message : null
      setSubmitError(reason || 'The setting could not be changed. Please try again in a moment.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const blocked = preview ? blockerMessage(preview) : null
  // The page was loaded before someone else changed the setting: there is nothing to switch.
  const alreadySet = !!preview && preview.currentIncludeWtt === includeWtt
  const periods = preview?.openPeriods ?? []
  // A locked period is named when the switch leaves it reporting the other way: still counting
  // well-to-tank after a switch off, or still excluding it after a switch on. Amounts below what
  // the reports print (0.01 t) are not worth a warning.
  const lockedPeriods = (preview?.lockedPeriods ?? []).filter(p => (includeWtt ? p.kgExcluded : p.kgCounting) >= LOCKED_PERIOD_THRESHOLD_KG)
  const baseYear = preview?.baseYear ?? null
  const baseYearUnreached = !!baseYear && baseYear.locked
  const baseYearListed = baseYearUnreached && !baseYear.archived && lockedPeriods.some(p => p.reportPeriodId === baseYear.reportPeriodId)

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      title={includeWtt ? 'Include well-to-tank emissions?' : 'Exclude well-to-tank emissions?'}
      cancelHandler={handleClose}
      submitHandler={handleConfirm}
      isSubmitting={isSubmitting}
      disableSaveButton={isLoading || !preview || !!blocked || alreadySet}
      disableCancelButton={isSubmitting}
      maxWidth='md'
      submitButtonText={includeWtt ? 'Include well-to-tank' : 'Exclude well-to-tank'}
      submitButtonLoadingText='Saving'
    >
      {isLoading ? <LoadingSpinner /> : (
        <Box>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Well-to-tank emissions come from producing and transporting the fuel and energy you use, before it
            reaches you. They are reported in Scope 3, mostly in Category 3.
          </Typography>

          {loadError && <Alert severity='error'>{loadError}</Alert>}
          {alreadySet && (
            <Alert severity='info' sx={{ mb: 2 }}>
              {includeWtt
                ? 'Well-to-tank emissions are already included. Someone may have changed this since the page loaded, so refresh it to see the current settings.'
                : 'Well-to-tank emissions are already excluded. Someone may have changed this since the page loaded, so refresh it to see the current settings.'}
            </Alert>
          )}
          {blocked && <Alert severity='warning' sx={{ mb: 2 }}>{blocked}</Alert>}

          {preview && !alreadySet && periods.length === 0 && (
            <Alert severity='info' sx={{ mb: 2 }}>
              Nothing changes in your open report periods.
              {includeWtt
                ? ' Records you add or edit from now on will include well-to-tank emissions.'
                : ' Records you add or edit from now on will not include well-to-tank emissions.'}
            </Alert>
          )}

          {preview && !alreadySet && periods.length > 0 && (
            <>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Report period</TableCell>
                    <TableCell>Where it reports</TableCell>
                    <TableCell align='right'>{includeWtt ? 'Added to reported totals' : 'Removed from reported totals'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periods.map(period => (
                    <TableRow key={period.reportPeriodId}>
                      <TableCell>{periodLabel(period)}</TableCell>
                      <TableCell>
                        {period.byGroup.map(group => (
                          <Typography key={group.groupId} variant='body2' sx={{ color: 'text.secondary' }}>
                            {groupLabel(group)}: {formatKg(group.kg)}
                          </Typography>
                        ))}
                      </TableCell>
                      <TableCell align='right'>{formatKg(period.kg)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box sx={{ mt: 2 }}>
                <Typography variant='body2'>
                  {includeWtt
                    ? `In total, ${formatKg(preview.totalKg)} will be added to your reported totals.`
                    : `In total, ${formatKg(preview.totalKg)} will be removed from your reported totals.`}
                </Typography>
                {!includeWtt && (
                  <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
                    Nothing is deleted. Each record keeps its well-to-tank lines, marked Not used in reporting, and
                    you can include them again at any time. The GHG report and audit pack for these periods will
                    say that well-to-tank emissions are excluded.
                  </Typography>
                )}
              </Box>
            </>
          )}

          {preview && !alreadySet && (
            <Box sx={{ mt: 2 }}>
              {lockedPeriods.length > 0 ? (
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  Locked report periods keep their figures, so {joinNames(lockedPeriods.map(periodLabel))}
                  {includeWtt ? ' will still exclude well-to-tank emissions' : ' will still include well-to-tank emissions'}.
                  {baseYearListed
                    ? ' That includes your base year, so comparisons with it will no longer be like for like.'
                    : ' Comparisons between those periods and your open ones will no longer be like for like.'}
                </Typography>
              ) : (
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  Locked report periods are never changed.
                </Typography>
              )}
              {baseYearUnreached && baseYear?.archived && (
                <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Your base year{baseYear.name ? ` (${baseYear.name})` : ''} holds totals entered by hand, so check
                  whether they include well-to-tank emissions before comparing with it.
                </Typography>
              )}
            </Box>
          )}

          {submitError && <Alert severity='error' sx={{ mt: 2 }}>{submitError}</Alert>}
        </Box>
      )}
    </DialogWrapper>
  )
}
